import { useState } from 'react';
import { Badge, Button, Group, Stack, Tabs } from '@mantine/core';
import { DataTableColumn } from 'mantine-datatable';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { JoinRequestSummary, JoinRequestStatus } from 'src/api/joinRequests';

type StatusFilter = 'all' | 'PENDING' | 'APPROVED' | 'REJECTED';

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

const statusBadgeColour: Record<JoinRequestStatus, string> = {
  PENDING: 'orange',
  APPROVED: 'green',
  REJECTED: 'red',
};

const statusLabel: Record<JoinRequestStatus, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

type Props = {
  onSelectRequest?: (id: string) => void;
  onRejectRequest?: (id: string) => void;
};

export default function JoinRequestsTab({ onSelectRequest, onRejectRequest }: Props) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const filters = statusFilter !== 'all' ? { status: statusFilter } : undefined;

  const columns: DataTableColumn<JoinRequestSummary>[] = [
    {
      accessor: 'createdAt',
      title: 'Submitted',
      render: row => formatDate(row.createdAt),
    },
    { accessor: 'companyName', title: 'Company name' },
    { accessor: 'websiteUrl', title: 'Website' },
    {
      accessor: 'status',
      title: 'Status',
      render: row => (
        <Badge color={statusBadgeColour[row.status]} variant="light">
          {statusLabel[row.status]}
        </Badge>
      ),
    },
    { accessor: 'signatoryEmail', title: 'Signatory email' },
    {
      accessor: 'actions',
      title: 'Actions',
      render: row => (
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            onClick={e => {
              e.stopPropagation();
              onSelectRequest?.(row.id);
            }}
          >
            View
          </Button>
          {row.status === 'PENDING' && (
            <Button
              size="xs"
              variant="subtle"
              color="red"
              onClick={e => {
                e.stopPropagation();
                onRejectRequest?.(row.id);
              }}
            >
              Reject
            </Button>
          )}
        </Group>
      ),
    },
  ];

  return (
    <Stack gap="md">
      <Tabs
        value={statusFilter}
        onChange={value => setStatusFilter((value as StatusFilter) ?? 'all')}
      >
        <Tabs.List>
          {STATUS_TABS.map(tab => (
            <Tabs.Tab key={tab.value} value={tab.value}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      <SimpleTable<JoinRequestSummary>
        key={statusFilter}
        resource="admin/join-requests"
        columns={columns}
        filters={filters}
        title="Join requests"
        showCount
        enableSearch
        pageSize={20}
        paginationDependencies={statusFilter}
        onRowClick={row => onSelectRequest?.(row.id)}
        searchAttributes={['companyName', 'websiteUrl', 'signatoryEmail']}
        useLocalPagination
      />
    </Stack>
  );
}
